import { requestIdText } from '@featit/shared/request-id';
import { DEPLOYMENT_SETUP_COMMAND, FIRST_RUN_CLOSED_CODE } from '@featit/shared/deployment-setup';
import {
  isSignedOutRefusalCode,
  SIGNED_OUT_REFUSALS,
  type SignedOutRefusalCodeType,
} from '@featit/shared/credentials';
import { isGenericServerText } from './failureReasons';
import {
  GraphqlRequestError,
  failureReference,
  isPermanentServerError,
  isReadTimeoutError,
  readTimeoutLimitMs,
} from './graphqlRequestError';
import {
  describeLoadFailure,
  LOAD_FAILURE_CATEGORY_LABELS,
  loadFailureReason,
  type ILoadFailure,
  type LoadFailureCategoryType,
} from './loadFailure';
import { ShellOfflineWriteError } from '@/shell/shellOffline';
import { desktopFailureReference } from '@/desktop/desktopFailureReference';
import { planRefusalSentence } from './planLimitRefusal';
import {
  refusedRoleIn,
  roleRefusalArgs,
  roleRefusalReason,
  roleRefusalSentence,
  type IRoleRefusalReason,
} from './roleRefusalReason';
import {
  readerRefusalArgsIn,
  readerRefusalReason,
  readerRefusalSentence,
  sayReaderRefusal,
} from './readerRefusalReason';
import { replyDraftAgentRefusalSentence } from './replyDraftAgentRefusal';
import { verificationReadOnlySentence } from './verificationReadOnly';
import { httpErrorMessage } from './ApiService';
import { failures } from '@featit/shared/i18n/messages/en/failures';
import type { MessageKey } from '@featit/shared/i18n/messages/index';
import { registerEnglishArea } from '@/i18n/catalogue';
import type { MessageArgs } from '@featit/shared/i18n/core';
import { englishT, type TFn } from '@/i18n/LocaleProvider';
import { pageT } from '@/i18n/pageTranslator';

registerEnglishArea('failures', failures);

/**
 * What a failed action says to the person who tried it (I-0398): the action, the kind of failure, and
 * what to do next.
 *
 * ⛔ **The only place a failure's message is read.** A rejection's own `message` is written for a
 * network log — "Request failed with status code 500", "Network Error" — and `errorMessages.guard.test`
 * fails any other file that renders one.
 *
 * ⭐ **The server's refusal sentence is kept where it was written for the reader.** A 4xx the deployment
 * explained — "This user does not hold "work_area.read" …", "A release with that name already exists"
 * — is the most useful thing on the screen, and replacing it with a category would throw the answer
 * away. A 5xx, a dropped request, a GraphQL validation failure and anything thrown in the browser get
 * the category's own sentence instead.
 */
export type ActionFailureCategoryType =
  | LoadFailureCategoryType
  | 'refused'
  | 'desktop'
  | 'not-possible-here'
  | 'changed-elsewhere';

/**
 * A sentence the web app itself wrote for the reader, thrown where it is decided.
 *
 * ⭐ The one way to put app-authored text through a `catch` and keep it: "Passkeys need a secure
 * connection, and this page is open at …" is exactly what the reader should see, and an ordinary
 * `Error` carrying it would be indistinguishable from a browser exception's "Cannot read properties of
 * undefined". Throw this instead, and `describeActionFailure` shows it.
 */
export class ReaderFacingError extends Error {
  /**
   * What kind of failure the sentence reports. `not-possible-here` unless the thrower knows better:
   * a save refused because somebody else saved first is `changed-elsewhere` (F-0288), and labelling
   * it "not possible here" would tell the reader the action itself is not allowed.
   */
  readonly category: Extract<ActionFailureCategoryType, 'not-possible-here' | 'changed-elsewhere'>;

  constructor(
    sentence: string,
    category: Extract<
      ActionFailureCategoryType,
      'not-possible-here' | 'changed-elsewhere'
    > = 'not-possible-here',
  ) {
    super(sentence);
    this.name = 'ReaderFacingError';
    this.category = category;
  }
}

export interface IActionFailure {
  category: ActionFailureCategoryType;
  /** "Couldn't save notification preferences." */
  title: string;
  /** What happened and what to do — the server's sentence, or the category's. */
  reason: string;
  /** Whether trying the same action again can succeed without anybody changing anything. */
  retryable: boolean;
  /** I-1470: the server's request id for the failure, for a technical detail; never the title. */
  reference?: string | null;
}

/** A failure as described inside this module: with its reason's catalogue key when FEATit wrote it. */
interface IKeyedActionFailure extends IActionFailure {
  /** I-2083: the headline's kind when the category's word would be wrong (a read nobody answered, or the server timed out). */
  kind?: 'stalled' | 'serverTimeout';
  /** The reason when it is FEATit's own sentence, so `actionFailureTextIn` says it in the reader's language. */
  reasonKey?: {
    key: MessageKey;
    args?: Record<string, string | number>;
    /** I-2195: the refused role, named in the reader's language when the reason is worded. */
    role?: IRoleRefusalReason['facts'];
    /** I-2439: a reader refusal's choice facts, said in the reader's language before the sentence. */
    parts?: Readonly<Record<string, MessageKey>>;
  };
}

export const ACTION_FAILURE_CATEGORY_LABELS: Record<ActionFailureCategoryType, string> = {
  ...LOAD_FAILURE_CATEGORY_LABELS,
  refused: 'Not accepted',
  desktop: 'This computer',
  'not-possible-here': 'Not possible here',
  'changed-elsewhere': 'Changed elsewhere',
};

const NOT_A_SENTENCE_CODES = new Set([
  'INTERNAL_SERVER_ERROR',
  'GRAPHQL_VALIDATION_FAILED',
  'GRAPHQL_PARSE_FAILED',
]);

function meaningful(text: unknown): string | null {
  if (Array.isArray(text)) {
    const sentences = text.filter((entry): entry is string => typeof entry === 'string');
    return meaningful(sentences.join(' '));
  }
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (!trimmed || isGenericServerText(trimmed)) return null;
  // A sentence the next one can follow: "Work area not found" would run into "You can still file".
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

/**
 * I-2439 (IN-104): what the server said about a refused request, for a screen that shows it in a line
 * of its own: a refusal named by its code in the page's language (a reader's, a role's, a plan's, a
 * verification session's, a drafting agent's, a signed-out door's), else the server's own sentence as
 * it came, whatever the status. Null when it said nothing a person reads. ⛔ A screen never reads
 * `response.data.message` itself (`server-message-reads.guard.test.ts`): it asks this.
 */
export function serverRefusalText(error: unknown): string | null {
  const coded =
    planRefusalSentence(error) ??
    roleRefusalSentence(error) ??
    verificationReadOnlySentence(error) ??
    readerRefusalSentence(error) ??
    replyDraftAgentRefusalSentence(error);
  if (coded) return coded;
  const data = (error as { response?: { data?: { code?: unknown; message?: unknown } } } | null)
    ?.response?.data;
  const code = data?.code;
  // A code this app has no sentence for falls through to the server's English, as an unknown one does.
  const key = isSignedOutRefusalCode(code) ? SIGNED_OUT_REFUSAL_KEYS[code] : undefined;
  if (key) return sayFailure(pageT(englishT), sentenceToSay(key));
  const message = data?.message;
  if (Array.isArray(message)) {
    const sentences = message.filter((entry): entry is string => typeof entry === 'string');
    return sentences.length > 0 ? sentences.join(' ') : null;
  }
  return typeof message === 'string' && message.trim() ? message : null;
}

/** The sentence the deployment wrote for the reader, when it wrote one. */
function serverSentence(error: unknown): string | null {
  // I-1712 (AR-7.12): in the iOS app a plan's refusal is said from its code, with no plan to buy.
  const planRefusal = planRefusalSentence(error);
  if (planRefusal) return planRefusal;
  // I-2189 siblings: a verification session's refused write, said from its code in the page's language.
  const readOnly = verificationReadOnlySentence(error);
  if (readOnly) return readOnly;
  // I-2439: a refusal named by its code, in the page's language.
  const coded = readerRefusalSentence(error);
  if (coded) return coded;
  // F-0786 PAG-7a: a drafting agent FEATit does not run, said from its code in the page's language.
  const draftAgent = replyDraftAgentRefusalSentence(error);
  if (draftAgent) return draftAgent;
  if (error instanceof GraphqlRequestError) {
    const code = String(error.extensions.code ?? '');
    const original = error.extensions.originalError as { statusCode?: unknown } | undefined;
    const status = typeof original?.statusCode === 'number' ? original.statusCode : null;
    if (NOT_A_SENTENCE_CODES.has(code) || (status !== null && status >= 500)) return null;
    return meaningful((error as { message?: unknown }).message);
  }
  const response = (
    error as { response?: { status?: unknown; data?: { message?: unknown } } } | null
  )?.response;
  if (typeof response?.status === 'number' && response.status >= 400 && response.status < 500) {
    return meaningful(response.data?.message);
  }
  return null;
}

/**
 * A failure the desktop shell reported about this computer, with the sentence its own code wrote.
 *
 * Tauri's `invoke` rejects with the command's `Err(String)` — a string, not an `Error` — and that
 * string is written in the desktop's Rust for the person at the machine ("launchd could not restart
 * the bridge: …"). The cloud gateway's `DesktopCloudTransportError` carries a status like any
 * transport, and `DesktopRuntimeUnavailableError` means the page is not running in the desktop app.
 */
function desktopFailure(error: unknown, action: string): IKeyedActionFailure | null {
  const title = `Couldn't ${action}.`;
  if (typeof error === 'string') {
    const sentence = meaningful(error);
    // F-0689 DG-9: the reference the app's log line holds, when the app logged it.
    const reference = desktopFailureReference(error);
    return {
      category: 'desktop',
      title,
      reason: sentence ?? 'The desktop app could not do this. Try again.',
      ...(sentence ? {} : { reasonKey: { key: 'failures.reasons.desktopCouldNot' } }),
      retryable: true,
      ...(reference ? { reference } : {}),
    };
  }
  const named = error as { name?: unknown; status?: unknown; message?: unknown } | null;
  if (named?.name === 'DesktopCommandFailedError') {
    const reference = desktopFailureReference(error);
    return {
      category: 'desktop',
      title,
      reason: meaningful(named.message) ?? 'The desktop app could not do this. Try again.',
      ...(meaningful(named.message)
        ? {}
        : { reasonKey: { key: 'failures.reasons.desktopCouldNot' } }),
      retryable: true,
      ...(reference ? { reference } : {}),
    };
  }
  if (named?.name === 'DesktopRuntimeUnavailableError') {
    return {
      category: 'desktop',
      title,
      reason: 'This needs the FEATit desktop app on this computer.',
      reasonKey: { key: 'failures.reasons.desktopNeeded' },
      retryable: false,
    };
  }
  if (named?.name === 'DesktopCloudTransportError' && typeof named.status === 'number') {
    if (named.status >= 500) {
      return describeFailureOf({ response: { status: named.status } }, action);
    }
    if (named.status >= 400) {
      const sentence = meaningful(named.message);
      if (sentence) return { category: 'refused', title, reason: sentence, retryable: false };
    }
  }
  return null;
}

/**
 * @param action what the person was doing, as a verb phrase: "save notification preferences".
 */
export function describeActionFailure(error: unknown, action: string): IActionFailure {
  // The key is this module's business; a caller reads the failure as it always has.
  const { reasonKey: _reasonKey, ...failure } = describeFailureOf(
    error,
    straightApostrophes(action),
  );
  return failure;
}

/**
 * Only the failure's category, which holds no words in any language: for a caller that decides on
 * the kind of failure (a server refusal or a failure to reach it) and says nothing of it itself.
 */
export function actionFailureCategory(error: unknown): ActionFailureCategoryType {
  return describeFailureOf(error, '').category;
}

/**
 * {@link describeActionFailure} in the reader's language (ASF-19): its title ("Nicht gelungen: …")
 * and FEATit's own reason; a server's sentence stays as the server wrote it.
 */
export function describeActionFailureIn(t: TFn, error: unknown, action: string): IActionFailure {
  const { reasonKey, ...failure } = describeFailureOf(error, straightApostrophes(action));
  return {
    ...failure,
    title: t('failures.title', { action: straightApostrophes(action) }),
    reason: reasonKey
      ? t(reasonKey.key, {
          ...(reasonKey.parts
            ? readerRefusalArgsIn(t, { args: reasonKey.args ?? {}, parts: reasonKey.parts })
            : reasonKey.args),
          ...(reasonKey.role
            ? roleRefusalArgs(reasonKey.role, refusedRoleIn(t, reasonKey.role))
            : {}),
        })
      : failure.reason,
  };
}

/**
 * The app writes the straight apostrophe ("Couldn't"), and the line it builds says the caller's
 * action after it: "read the item types’ rules" read "Couldn't read the item types’ rules", two
 * kinds in one sentence (I-1319).
 */
function straightApostrophes(text: string): string {
  return text.replace(/\u2019/g, "'");
}

function describeFailureOf(error: unknown, action: string): IKeyedActionFailure {
  // F-0236 (AC-11): refused before anything was sent, never queued. The reason is the shell's own
  // sentence, not the server's — there was no request for the server to answer.
  if (error instanceof ShellOfflineWriteError) {
    return {
      category: 'offline',
      title: `Couldn't ${action}.`,
      reason: error.message,
      reasonKey: { key: 'failures.reasons.offlineWrite' },
      retryable: true,
    };
  }
  if (error instanceof ReaderFacingError) {
    return {
      category: error.category,
      title: `Couldn't ${action}.`,
      reason:
        meaningful((error as { message?: unknown }).message) ??
        loadFailureReason({ category: 'unknown', permission: null }),
      // I-1976: the thrower's sentence is already the reader's; the fallback is FEATit's own words.
      ...(meaningful((error as { message?: unknown }).message)
        ? {}
        : { reasonKey: loadFailureReasonKey({ category: 'unknown', permission: null }) }),
      retryable: false,
    };
  }
  const desktop = desktopFailure(error, action);
  if (desktop) return desktop;
  const load: ILoadFailure = describeLoadFailure(error);
  // I-2111: a 408 is FEATit's own answer, worded here from its limit (never the server's English).
  if (load.serverTimedOut) {
    const reasonKey = readTimeoutReasonKey(error);
    return {
      category: 'server',
      title: `Couldn't ${action}.`,
      reason: englishT(reasonKey.key, reasonKey.args),
      reasonKey,
      kind: 'serverTimeout' as const,
      retryable: !isPermanentServerError(error),
      ...(load.reference ? { reference: load.reference } : {}),
    };
  }
  // I-2195: a refusal that names a role is worded from its code and the role's facts.
  const roleRefusal = load.category === 'offline' ? null : roleRefusalReason(error);
  if (roleRefusal) {
    const args = roleRefusalArgs(roleRefusal.facts, roleRefusal.facts.roleNames.en);
    return {
      category: 'refused',
      title: `Couldn't ${action}.`,
      reason: englishT(roleRefusal.key, args),
      reasonKey: { key: roleRefusal.key, args, role: roleRefusal.facts },
      retryable: false,
      ...(load.reference ? { reference: load.reference } : {}),
    };
  }
  // I-2439: a refusal the server named by its code is said from the catalogue, its English the
  // fallback for a code this app does not know.
  const readerRefusal = load.category === 'offline' ? null : readerRefusalReason(error);
  if (readerRefusal) {
    return {
      category: load.category === 'unknown' ? 'refused' : load.category,
      title: englishT('failures.title', { action }),
      reason: sayReaderRefusal(englishT, readerRefusal),
      reasonKey: { key: readerRefusal.key, args: readerRefusal.args, parts: readerRefusal.parts },
      retryable: false,
      ...(load.reference ? { reference: load.reference } : {}),
    };
  }
  const sentence = load.category === 'offline' ? null : serverSentence(error);
  const category: ActionFailureCategoryType =
    sentence && load.category === 'unknown' ? 'refused' : load.category;
  return {
    category,
    title: `Couldn't ${action}.`,
    reason: sentence ?? loadFailureReason(load),
    ...(sentence ? {} : { reasonKey: loadFailureReasonKey(load) }),
    ...(load.stalled ? { kind: 'stalled' as const } : {}),
    ...(load.serverTimedOut ? { kind: 'serverTimeout' as const } : {}),
    // I-1015: a server rejection stamped `permanent` overrides the category guess below — the same
    // payload will refuse the same way every time, whatever the category heuristic made of the
    // message that came with it.
    retryable:
      !isPermanentServerError(error) &&
      (category === 'offline' || category === 'server' || category === 'unknown'),
    ...(load.reference ? { reference: load.reference } : {}),
  };
}

/**
 * The sentence for a read the server stopped at its limit (I-2111): the limit it sent, in seconds, in
 * `failures.reasons.serverTimeoutLimit`; the generic sentence when an older server sent none.
 */
function readTimeoutReasonKey(error: unknown): NonNullable<IKeyedActionFailure['reasonKey']> {
  const limitMs = readTimeoutLimitMs(error);
  return limitMs === null
    ? { key: 'failures.reasons.serverTimeout' }
    : {
        key: 'failures.reasons.serverTimeoutLimit',
        args: { seconds: Math.round(limitMs / 100) / 10 },
      };
}

/**
 * F-0621: `httpErrorMessage` with its reasons in `t`'s language — the same `failures.reasons` keys
 * {@link sayFailure} says a failure's reason by — for a screen that keeps a refused request and
 * writes it at render. The server's own sentence and the request id are as they came.
 */
export function httpErrorMessageIn(t: TFn, error: unknown, fallback: string): string {
  return httpErrorMessage(error, fallback, {
    offline: t('failures.reasons.offline'),
    stalled: t('failures.reasons.stalled'),
    server: t('failures.reasons.server'),
  });
}

/** A failed read, as the one notice that reports it (I-1306). */
/** `loadFailureReason`'s sentence as a catalogue key (`failures.reasons`), case for case. */
function loadFailureReasonKey(
  failure: ILoadFailure,
): NonNullable<IKeyedActionFailure['reasonKey']> {
  switch (failure.category) {
    case 'permission':
      return failure.permission
        ? { key: 'failures.reasons.permission', args: { permission: failure.permission } }
        : { key: 'failures.reasons.permissionUnnamed' };
    case 'offline':
      return { key: failure.stalled ? 'failures.reasons.stalled' : 'failures.reasons.offline' };
    case 'server':
      return {
        key: failure.serverTimedOut ? 'failures.reasons.serverTimeout' : 'failures.reasons.server',
      };
    default:
      return { key: 'failures.reasons.unknown' };
  }
}

/** The kind a failure's headline names, as `failures.kinds` says it. */
const FAILURE_KIND_KEYS: Record<ActionFailureCategoryType, MessageKey> = {
  permission: 'failures.kinds.permission',
  offline: 'failures.kinds.offline',
  server: 'failures.kinds.server',
  unknown: 'failures.kinds.unknown',
  refused: 'failures.kinds.refused',
  desktop: 'failures.kinds.desktop',
  'not-possible-here': 'failures.kinds.notPossibleHere',
  'changed-elsewhere': 'failures.kinds.changedElsewhere',
};

/** The kinds a read that got no answer is named by, apart from `offline` (I-2083). */
const FAILURE_TIMING_KIND_KEYS = {
  stalled: 'failures.kinds.stalled',
  serverTimeout: 'failures.kinds.serverTimeout',
} as const satisfies Record<'stalled' | 'serverTimeout', MessageKey>;

/** A failure's kind in the reader's language, as its headline names it: "Serverstörung". */
export function failureKindIn(t: TFn, category: ActionFailureCategoryType): string {
  return t(FAILURE_KIND_KEYS[category]);
}

export interface IReadFailure {
  /** What the notice says: the server's own sentence for a refusal that cannot change. */
  message: string;
  /** I-1470: the server's request id for the failure, drawn small beside the sentence, never in it. */
  reference: string | null;
  /**
   * Whether asking the same thing again can succeed. False is a refusal the server marked
   * permanent: the notice offers no "Try again" and promises no retry, since what repairs it is in
   * the sentence and the control that repairs it is the one the reader changes.
   */
  retryable: boolean;
  /**
   * I-1305: the read ran out of time, on the server or on the way. Asked again, it costs the same
   * time again, so whoever retries it does so less and less often and says when.
   */
  timedOut: boolean;
}

/**
 * How a failed **read** is shown, for every view and list (I-1306).
 *
 * A read the server refused for good, with a sentence of its own, is that sentence and nothing
 * else: "A search takes at most 32 different words, and this one has 33. Remove some words and
 * search again." It was drawn behind the format of a failed action, "Couldn't load this dataset
 * (not accepted). …", beside a "Try again" that asked the same refused question. A read the server
 * stopped for running too long is its sentence too, and can be asked again (I-1305). Any other
 * failed read keeps {@link actionFailureText}'s sentence and can be asked again.
 *
 * Deliberately as narrow as the mark it reads: `isPermanentServerError` is the deployment's own
 * `extensions.permanent`, never this file's category guess, which would turn failures a view has
 * always retried into dead ends (I-1015).
 *
 * @param action what was being read, as a verb phrase: "load this dataset".
 */
export function describeReadFailure(error: unknown, action: string): IReadFailure {
  return readFailureOf(error, action, () =>
    actionFailureText(error, action, { withReference: false }),
  );
}

/** A failed read, its ordinary sentence said by `said`. */
function readFailureOf(error: unknown, action: string, said: () => string): IReadFailure {
  const failure = describeActionFailure(error, action);
  const reference = failure.reference ?? null;
  const refusal = isPermanentServerError(error) ? serverSentence(error) : null;
  if (refusal) return { message: refusal, reference, retryable: false, timedOut: false };
  // I-1305: a query the server stopped at its limit says so, naming the limit and what to do. It is
  // not a refusal for good: the same query may answer later. I-2111: its headline names the timeout
  // and the sentence below it is FEATit's, worded from the limit in the reader's language.
  const timedOut = isReadTimeoutError(error);
  return {
    message: said(),
    reference,
    retryable: !isPermanentServerError(error),
    timedOut,
  };
}

/**
 * I-0935 EM-4: a screen's own sentence for a failure, ending with the describer's reference (the
 * server's request id for it, I-1470 EM-9), as `actionFailureText` ends its line. For the screens
 * that word a failure themselves rather than take the describer's words; a failure with no request
 * id reads as the sentence alone.
 */
export function withFailureReference(sentence: string, error: unknown): string {
  // The reference the describers carry (`describeLoadFailure(error).reference`), read where they
  // read it, so a screen whose tests stand in for the HTTP layer still words its failure.
  const reference = failureReference(error);
  if (!reference) return sentence;
  return `${sentence.trim().replace(/[.]?$/, '.')} ${requestIdText(reference)}.`;
}

/**
 * The failure as one line, for the many screens that hold an error as a string:
 * "Couldn't save notification preferences (server error). The server ran into an error. Try again; …"
 */
export function actionFailureText(
  error: unknown,
  action: string,
  { withReference = true }: { withReference?: boolean } = {},
): string {
  const failure = describeFailureOf(error, straightApostrophes(action));
  const label = failure.kind
    ? failures.kinds[failure.kind]
    : ACTION_FAILURE_CATEGORY_LABELS[failure.category].toLowerCase();
  // I-0935: after the reason, never in the headline, so a screenshot places the failure in the
  // server's log (I-1470: by its request id). A screen that draws the reference on a line of its own
  // asks for the sentence without it.
  const reference =
    withReference && failure.reference ? ` ${requestIdText(failure.reference)}.` : '';
  return `${failure.title.replace(/\.$/, '')} (${label}). ${failure.reason}${reference}`;
}

/**
 * F-0160 AC-24: the one deliberate, narrow exception to "never a 5xx's own text" (see `serverSentence`
 * above, and the module header — a 500 is usually an accident with nothing useful to say).
 * `MailNotConfiguredError` (503) and `MailDeliveryFailedError` (502) are the two the concept names as
 * administrator-actionable refusals, not accidents, at exactly three doors: sign-up, password-reset
 * request, and verification resend. Swallowing either into the generic "Server error. Try again."
 * would hide the one thing a reader can act on — tell an administrator to open Settings → Email — and
 * is exactly the silent-failure class this whole lead exists to close.
 *
 * Returns `null` for anything else, so a caller falls through to `actionFailureText` unchanged.
 */
export function mailRefusalMessage(error: unknown): HeldSentenceType | null {
  const response = (
    error as {
      response?: { status?: unknown; data?: { code?: unknown; message?: unknown } };
    } | null
  )?.response;
  const status = typeof response?.status === 'number' ? response.status : null;
  if (status !== 502 && status !== 503) return null;
  return signedOutRefusalSentence(response?.data);
}

/**
 * F-0160 AC-16/AC-17/AC-22: a second, equally narrow exception, for a different reason than
 * `mailRefusalMessage` above. `EmailVerificationController.confirm` and
 * `PasswordResetController.confirm` (and its review's `remove`, T-0633) answer an invalid, expired or
 * already-used token with 401 and one
 * complete, deliberately generic sentence — "This verification link is invalid or has expired." —
 * chosen precisely so it needs no further categorising (§5's own mutation names it "one generic
 * message" for exactly this reason). `describeActionFailure`'s general 401 handling reads as
 * "permission", which is true of most 401s and wrong here: there is no session on this door for
 * permission to be about, and "Couldn't confirm this email address (permission)." tells a new person
 * they did something wrong when the honest fact is that a link they clicked has simply expired.
 *
 * Scoped to exactly the two doors that mint this shape of 401 — anything else's 401 still reads as a
 * permission refusal, correctly.
 */
export function expiredTokenMessage(error: unknown): HeldSentenceType | null {
  const response = (
    error as {
      response?: { status?: unknown; data?: { code?: unknown; message?: unknown } };
    } | null
  )?.response;
  if (response?.status !== 401) return null;
  // I-2439: a door that names its refusal is said from the code, in the reader's language; an older
  // server's sentence, or one whose code this app does not know, is shown as it came.
  return signedOutRefusalSentence(response.data);
}

/** I-2439: each signed-out door's refusal sentence (`auth.linkRefusals`, `auth.setupRefusals`). */
const SIGNED_OUT_REFUSAL_KEYS: Record<SignedOutRefusalCodeType, MessageKey> = {
  [SIGNED_OUT_REFUSALS.VERIFICATION_LINK]: 'auth.linkRefusals.verificationLink',
  [SIGNED_OUT_REFUSALS.PASSWORD_RESET_LINK]: 'auth.linkRefusals.passwordResetLink',
  [SIGNED_OUT_REFUSALS.PASSWORD_RESET_REVIEW]: 'auth.linkRefusals.passwordResetReview',
  [SIGNED_OUT_REFUSALS.SIGN_IN_ADDRESS_LINK]: 'auth.linkRefusals.signInAddressLink',
  [SIGNED_OUT_REFUSALS.UNSUBSCRIBE_LINK]: 'auth.linkRefusals.unsubscribeLink',
  [SIGNED_OUT_REFUSALS.SETUP_TOKEN]: 'auth.setupRefusals.setupToken',
  [SIGNED_OUT_REFUSALS.SETUP_NOT_NEEDED]: 'auth.setupRefusals.notNeeded',
  [SIGNED_OUT_REFUSALS.SETUP_ACCOUNT_DEACTIVATED]: 'auth.setupRefusals.accountDeactivated',
  [SIGNED_OUT_REFUSALS.SETUP_WRONG_PASSWORD]: 'auth.setupRefusals.wrongPassword',
  [SIGNED_OUT_REFUSALS.SETUP_ADDRESS_TAKEN]: 'auth.setupRefusals.addressTaken',
  [SIGNED_OUT_REFUSALS.CLAIM_LINK]: 'auth.setupRefusals.claimLink',
  [SIGNED_OUT_REFUSALS.SETUP_ADDRESS_INVALID]: 'auth.setupRefusals.addressInvalid',
  [SIGNED_OUT_REFUSALS.MAIL_NOT_SET_UP]: 'auth.mailRefusals.notSetUp',
  [SIGNED_OUT_REFUSALS.MAIL_NOT_ACCEPTED]: 'auth.mailRefusals.notAccepted',
  [SIGNED_OUT_REFUSALS.INVALID_CREDENTIALS]: 'auth.signInFailure.noMatch',
  [SIGNED_OUT_REFUSALS.PASSKEY_NOT_VALID]: 'auth.signInFailure.passkeyNotValid',
  [SIGNED_OUT_REFUSALS.PASSKEY_NOT_VERIFIED]: 'auth.signInFailure.passkeyNotVerified',
  [SIGNED_OUT_REFUSALS.SIGN_UP_ADDRESS_TAKEN]: 'auth.signInFailure.signUpAddressTaken',
  [SIGNED_OUT_REFUSALS.SIGN_UP_ADDRESS_MISSING]: 'auth.signInFailure.signUpAddressMissing',
};

/**
 * I-2439: a signed-out door's refusal body said in the reader's language when it names a code this
 * app knows; the server's sentence as it came for an older server or a code this app does not know.
 */
function signedOutRefusalSentence(
  body: { code?: unknown; message?: unknown; minutes?: unknown } | undefined,
): HeldSentenceType | null {
  const code = body?.code;
  if (isSignedOutRefusalCode(code)) return sentenceToSay(SIGNED_OUT_REFUSAL_KEYS[code]);
  // The browser setup door's window, closed or off, with its length when the server sent it.
  if (code === FIRST_RUN_CLOSED_CODE && typeof body?.minutes === 'number') {
    const command = DEPLOYMENT_SETUP_COMMAND;
    return body.minutes > 0
      ? sentenceToSay('auth.setupRefusals.firstRunClosed', { minutes: body.minutes, command })
      : sentenceToSay('auth.setupRefusals.firstRunOff', { command });
  }
  return meaningful(body?.message);
}

/**
 * I-2072: how many whole minutes a refused password-reset call says to wait, or `null` when the
 * failure is not that refusal. The server's body carries a `code` and the same `retryAfterSeconds` it
 * sets as `Retry-After`; its English `message` is never shown, so a German reader reads German.
 */
export function passwordResetRateLimitMinutes(error: unknown): number | null {
  const response = (
    error as {
      response?: { status?: unknown; data?: { code?: unknown; retryAfterSeconds?: unknown } };
    } | null
  )?.response;
  if (response?.status !== 429 || response.data?.code !== 'password_reset_rate_limited') {
    return null;
  }
  const seconds = Number(response.data.retryAfterSeconds);
  return Math.max(1, Math.ceil((Number.isFinite(seconds) && seconds > 0 ? seconds : 60) / 60));
}

/** {@link passwordResetRateLimitMinutes} as a sentence a screen keeps and says in its own language. */
export function passwordResetRateLimitSentence(error: unknown): ISentenceToSay | null {
  const minutes = passwordResetRateLimitMinutes(error);
  return minutes === null ? null : sentenceToSay('signedOut.tooManyAttempts', { minutes });
}

/**
 * F-0160 §11 (I-0638): what `POST /auth/setup` refused, in the server's own words.
 *
 * Every refusal on that door is one complete sentence written for the person holding the token — a
 * token that is invalid, expired or spent (401), an address whose password was not proven (401), a
 * deactivated account (403), a deployment that already has an administrator (404). None of them is
 * a permission problem, so the general 401/403 reading ("permission") would tell the operator the
 * wrong thing. Anything else — a network failure, a 500 — falls through to `actionFailureText`.
 */
/**
 * I-2439 (IN-103): a spent or unknown unsubscribe link (404), said from its code in the reader's
 * language; an older server's sentence as it came. Null for any other failure.
 */
export function unsubscribeRefusalMessage(error: unknown): HeldSentenceType | null {
  const response = (
    error as {
      response?: { status?: unknown; data?: { code?: unknown; message?: unknown } };
    } | null
  )?.response;
  if (response?.status !== 404) return null;
  return signedOutRefusalSentence(response.data);
}

export function setupRefusalMessage(error: unknown): HeldSentenceType | null {
  const response = (
    error as {
      response?: { status?: unknown; data?: { code?: unknown; message?: unknown } };
    } | null
  )?.response;
  // 409: the browser and agent doors refuse an address that already has an account, by name. 400
  // only when it names a code (I-2439): any other 400 keeps the failure rule's headline.
  const coded400 = response?.status === 400 && isSignedOutRefusalCode(response.data?.code);
  if (!coded400 && ![401, 403, 404, 409].includes(response?.status as number)) return null;
  return signedOutRefusalSentence(response?.data);
}

/**
 * `actionFailureText` in the reader's language: the headline and FEATit's own reason come from the
 * catalogue (`failures`), so a German screen never reads "Couldn't <German phrase>". `action` is the
 * caller's phrase in the same language (`t('…Action')`); a server's sentence is shown as it came, and
 * the request id keeps the form `readShownRequestId` reads back.
 */
export function actionFailureTextIn(
  t: TFn,
  error: unknown,
  action: string,
  { withReference = true }: { withReference?: boolean } = {},
): string {
  const failure = describeFailureOf(error, straightApostrophes(action));
  const headline = t('failures.headline', {
    action: straightApostrophes(action),
    kind: t(
      failure.kind ? FAILURE_TIMING_KIND_KEYS[failure.kind] : FAILURE_KIND_KEYS[failure.category],
    ),
  });
  const reason = failure.reasonKey
    ? t(failure.reasonKey.key, failure.reasonKey.args)
    : failure.reason;
  const reference =
    withReference && failure.reference ? ` ${requestIdText(failure.reference)}.` : '';
  return `${headline} ${reason}${reference}`;
}

/**
 * The open page's translator, for a failure worded outside a component, as a store words one
 * (I-1925's `pageT`): English until a page has rendered. A component asks `useT()` instead.
 */
export function pageWords(): TFn {
  return pageT(englishT);
}

/**
 * {@link actionFailureTextIn} for a store: the action by its key, said in the open page's language.
 * `pageFailureText(error, 'workItems.actions.loadItems')`.
 */
export function pageFailureText(
  error: unknown,
  action: MessageKey,
  args?: MessageArgs,
  options?: { withReference?: boolean },
): string {
  const t = pageWords();
  return actionFailureTextIn(t, error, t(action, args), options);
}

/**
 * A failure kept as what failed, not as a sentence: the screen writes it with {@link sayFailure} at
 * render time, so it follows the reader's language when that changes, and a load that fails needs
 * no `t` of its own (a load effect that names `t` would load again when German arrives).
 */
export interface IFailureToSay {
  readonly error: unknown;
  readonly action: MessageKey;
  readonly args?: MessageArgs;
  readonly withReference?: boolean;
  /** A line said after the failure's sentence, such as what it means for the list. */
  readonly after?: MessageKey;
  /** A failed read, said by the read rule (`describeReadFailureIn`): a refusal for good is its own. */
  readonly read?: boolean;
}

export function failureToSay(
  error: unknown,
  action: MessageKey,
  args?: MessageArgs,
  options?: { withReference?: boolean; after?: MessageKey; read?: boolean },
): IFailureToSay {
  return {
    error,
    action,
    args,
    withReference: options?.withReference,
    after: options?.after,
    read: options?.read,
  };
}

/** A line a screen keeps by its key, for the same reason as {@link IFailureToSay}. */
export interface ISentenceToSay {
  readonly say: MessageKey;
  readonly args?: MessageArgs;
  /** A failure whose reference the line ends with (`withFailureReference`). */
  readonly referenceOf?: unknown;
}

export function sentenceToSay(
  say: MessageKey,
  args?: MessageArgs,
  options?: { referenceOf?: unknown },
): ISentenceToSay {
  return options?.referenceOf === undefined
    ? { say, args }
    : { say, args, referenceOf: options.referenceOf };
}

/**
 * What a screen holds to say: a kept failure, a kept line, a sentence already written, or several
 * of them said one after the other.
 */
export type HeldSentenceType =
  | IFailureToSay
  | ISentenceToSay
  | string
  | readonly HeldSentenceType[];

/** The sentence for what a screen holds, in `t`'s language; a written sentence as it is. */
export function sayFailure(t: TFn, held: HeldSentenceType): string;
export function sayFailure(t: TFn, held: HeldSentenceType | null | undefined): string | null;
export function sayFailure(t: TFn, held: HeldSentenceType | null | undefined): string | null {
  if (held == null) return null;
  if (typeof held === 'string') return held;
  if (Array.isArray(held))
    return held.map((each: HeldSentenceType) => sayFailure(t, each)).join(' ');
  const one = held as IFailureToSay | ISentenceToSay;
  if ('say' in one) {
    const line = t(one.say, one.args);
    return one.referenceOf === undefined ? line : withFailureReference(line, one.referenceOf);
  }
  if (one.read) return describeReadFailureIn(t, one.error, t(one.action, one.args)).message;
  const sentence = actionFailureTextIn(t, one.error, t(one.action, one.args), {
    withReference: one.withReference ?? true,
  });
  return one.after ? `${sentence} ${t(one.after)}` : sentence;
}

/** {@link sayFailure} for a store's failures by key (one per work area, say). */
export function sayEach(
  t: TFn,
  held: Record<string, HeldSentenceType | undefined>,
): Record<string, string | undefined> {
  return Object.fromEntries(
    Object.entries(held).map(([key, value]) => [
      key,
      value === undefined ? undefined : sayFailure(t, value),
    ]),
  );
}

/** {@link describeReadFailure} in the reader's language: its message is `actionFailureTextIn`'s. */
export function describeReadFailureIn(t: TFn, error: unknown, action: string): IReadFailure {
  return readFailureOf(error, action, () =>
    actionFailureTextIn(t, error, action, { withReference: false }),
  );
}
